// All visible texts (SW-GE-01).
export const fr = {
  appName: "Secours Nav",
  nav: { routeTest: "Test d'itinéraire", events: "Événements", app: "Application", admin: "Administration", logout: "Se déconnecter", account: "Mon compte" },
  common: {
    save: "Enregistrer", cancel: "Annuler", delete: "Supprimer", close: "Fermer", create: "Créer", edit: "Modifier", back: "Retour",
    yes: "Oui", no: "Non", loading: "Chargement…", saving: "Enregistrement…", search: "Rechercher", confirm: "Confirmer", refresh: "Actualiser",
    required: "obligatoire", optional: "facultatif", none: "Aucun", unknown: "inconnu", by: "par", version: "version", actions: "Actions",
    copied: "Copié", copy: "Copier", print: "Imprimer", download: "Télécharger", filter: "Filtrer", all: "Tous", more: "Suite",
    reset: "Réinitialiser", add: "Ajouter", apply: "Appliquer", retry: "Réessayer", offline: "Connexion au serveur perdue : les modifications ne sont pas possibles. Nouvelle tentative…",
    offlineSince: (t: string) => `Connexion au serveur perdue : les données affichées datent de ${t}. Les modifications ne sont pas possibles. Nouvelle tentative…`,
    offlineShort: "Hors ligne : enregistrement impossible", loadingModule: "Chargement de l'écran…",
    online: "Connexion rétablie.", conflict: "Cet objet a été modifié par un autre opérateur. Vérifiez les valeurs actuelles puis enregistrez de nouveau.",
    reloadCurrent: "Charger la version actuelle",
    changedMeanwhile: (by?: string, at?: string) => `Cet objet vient d'être modifié${by ? ` par ${by}` : ""}${at ? ` (${at})` : ""} pendant votre saisie. Votre saisie est conservée. Si vous enregistrez, les deux versions vous seront présentées.`,
  },
  login: {
    title: "Connexion",
    intro: "Console Secours Nav. Accès réservé aux opérateurs et administrateurs.",
    login: "Identifiant", password: "Mot de passe", submit: "Se connecter", busy: "Connexion…",
    INVALID_CREDENTIALS: "Identifiant ou mot de passe incorrect.", LOGIN_LOCKED: "Trop d'échecs : réessayez dans 15 minutes.",
    mustChange: "Votre mot de passe est provisoire : choisissez-en un nouveau pour continuer.",
    changeTitle: "Changer de mot de passe", current: "Mot de passe actuel", newPassword: "Nouveau mot de passe", confirmPassword: "Confirmation",
    mismatch: "Les deux mots de passe diffèrent.", tooShort: "12 caractères au minimum.", changed: "Mot de passe modifié.",
    sessionInfo: "Session de 12 heures glissantes, 7 jours au maximum.",
  },
  events: {
    title: "Événements",
    intro: "Préparez les événements, publiez-les aux équipages et suivez les modifications.",
    open: "Ouvrir", test: "Tester",
    newEvent: "Nouvel événement", name: "Nom", description: "Description", start: "Début", end: "Fin",
    defaultCategories: "Catégories de POI à créer", pickAll: "Tout cocher", pickNone: "Tout décocher",
    noDefaultCategory: "Aucune catégorie par défaut n'est définie par l'administrateur.",
    categoriesLater: "Vous pourrez créer, modifier ou masquer des catégories à tout moment depuis l'onglet Catégories de l'événement.",
    restrictions: (n: number) => `${n} restriction${n > 1 ? "s" : ""}`,
    pois: (n: number) => `${n} POI`,
    invalid: "Fichier invalide",
    none: "Aucun événement. Créez le premier.",
    noneFiltered: "Aucun événement dans ce filtre.",
    local: "Fichier local",
    openFile: "Tester un fichier d'événement",
    openFileHelp: "Fichier *.event.json (format 1). Il reste dans le navigateur, rien n'est enregistré sur le serveur.",
    status: { DRAFT: "Brouillon", PUBLISHED: "Publié", ARCHIVED: "Archivé" } as Record<string, string>,
    phase: { UPCOMING: "À venir", ONGOING: "En cours", ENDED: "Terminé" } as Record<string, string>,
    filterStatus: "Statut", filterPhase: "Phase", searchName: "Rechercher un événement", updated: "Modifié", revision: "révision",
    endBeforeStart: "La fin doit être postérieure au début.",
    created: "Événement créé.", pending: (n: number) => `${n} signalement${n > 1 ? "s" : ""} à traiter`,
  },
  workspace: {
    tabs: { objects: "Objets", categories: "Catégories", history: "Historique", settings: "Réglages", share: "Partage" },
    revision: (r: number) => `révision ${r}`,
    liveOk: "Synchronisé", livePolling: "Canal temps réel coupé : relevé des changements toutes les 15 s",
    liveOffline: "Serveur injoignable", lastSync: (t: string) => `synchronisé à ${t}`,
    changedBy: (what: string, who: string) => `${what} modifié par ${who}`,
    changedByMany: (n: number, who: string) => `${n} objets modifiés par ${who}`,
    deletedBy: (what: string, who: string) => `${what} supprimé par ${who}`,
    statusAt: "Statuts à l'heure", transitionsHint: "Instants où un statut change. Cliquez pour simuler cette heure.",
    outsidePacks: "Hors de la zone cartographiée par les packs régionaux",
    publish: "Publier", archive: "Archiver", unarchive: "Rouvrir", deleteEvent: "Supprimer l'événement", checklist: "Vérifications avant publication",
    publishConfirmActive: "L'événement est en cours : les modifications sont diffusées immédiatement aux équipages. Publier ?",
    published: "Événement publié : les appareils reçoivent le dispositif.", archived: "Événement archivé : lecture seule.",
    unarchived: "Événement rouvert.", readOnly: "Événement archivé : lecture seule.",
    deleteTitle: "Supprimer l'événement", deleteText: "Suppression définitive de l'événement, de ses objets et de son historique. Saisissez son nom pour confirmer.",
    deleteName: "Nom de l'événement",
    diffusion: "Événement publié : chaque enregistrement est diffusé aux équipages dès maintenant.",
    activeWarning: "Événement publié et en cours : cette modification sera appliquée immédiatement dans les véhicules.",
    draw: { point: "Placer un point", path: "Tracer un tronçon", poi: "Placer un POI", zone: "Dessiner la zone", stop: "Terminer", cancel: "Annuler le dessin",
      pathHint: "Cliquez les points de passage le long de la voie, puis Terminer (ou double-clic). Le tracé est guidé par le réseau.",
      pointHint: "Cliquez sur la voie à bloquer ou limiter.", zoneHint: "Cliquez les sommets de la zone, puis Terminer (au moins 3 points).",
      poiHint: "Cliquez l'emplacement du point d'intérêt." },
    layers: "Couches", zone: "Zone", zoneAdjusted: "Zone ajustée (envoyée aux appareils)", effects: "Effets calculés", drawn: "Objets saisis", pois: "POI", hc: "Corrections de hauteur",
    search: "Lieu, commerce, adresse, commune ou Plus Code", noHit: "Aucun résultat", 
    hitKind: { eventPoi: "POI de l'événement", place: "Lieu", address: "Adresse", street: "Rue", commune: "Commune", pluscode: "Plus Code" } as Record<string, string>,
    objectsOutside: (n: number) => `${n} objet${n > 1 ? "s" : ""} hors de la zone dessinée`,
    zoneExtended: "L'objet est hors de la zone dessinée : la zone envoyée aux appareils a été étendue.",
    noObjects: "Aucune restriction ni POI. Placez un point, tracez un tronçon ou importez un fichier.",
    import: "Importer un fichier", now: "maintenant", simulated: "Heure simulée",
  },
  restriction: {
    title: "Restriction", new: (k: string) => `Nouvelle restriction : ${k}`, kind: "Type", geometry: "Géométrie", redraw: "Redessiner",
    point: "point", path: (n: number) => `tronçon, ${n} points`, coursePath: (n: number) => `tracé, ${n} points`, label: "Libellé (voies)", courseLabel: "Libellé du parcours", comment: "Commentaire",
    enabled: "Active", disabled: "Désactivée", period: "Période", eventPeriod: "Toute la durée de l'événement", custom: "Période propre",
    startAt: "Début", endAt: "Fin", crossable: "Franchissable par les secours", crossableHelp: "Barrière ouvrable ou plot amovible : les secours peuvent passer en urgence.",
    deviceType: "Dispositif", deviceTypes: { FENCE: "Barrière", ANTI_RAM_VEHICLE: "Véhicule anti-bélier", CONCRETE_BLOCK: "Bloc béton", OTHER: "Autre" } as Record<string, string>,
    blockedBearing: "Sens bloqué (cap en degrés)", blockedBearingHelp: "Vide : les deux sens sont bloqués.",
    maxHeight: "Hauteur limite (m)", oldDirection: "Ancien sens", snapping: "Accrochage", 
    snapping_none: (r: number) => `Aucune voie carrossable à moins de ${r} m : déplacez le point sur la voie.`, snapNotes: "Voies concernées",
    snapNoPath: "Le tracé ne suit aucune voie du réseau : redessinez-le sur la voie.", snapPick: "Cliquez une voie pour y déplacer le point.",
    unnamed: "voie sans nom", deleteConfirm: "Supprimer cette restriction ?",
    saved: "Restriction enregistrée.", deleted: "Restriction supprimée.",
    chain: "Créer à la suite", chainHelp: "Nouvelle restriction du même type qui commence à la fin de celle-ci : cliquez ensuite sa position.",
    chainStarted: (t: string) => `Nouvelle restriction à partir du ${t} : placez-la sur la carte.`,
    enable: "Activer", disable: "Désactiver",
    bulkEnable: "Activer la sélection", bulkDisable: "Désactiver la sélection", selected: (n: number) => `${n} sélectionnée${n > 1 ? "s" : ""}`,
    noEnd: "sans fin propre",
    kindHelp: {
      BARRIER: "Point sur la voie ou trait en travers des voies à fermer.",
      ONEWAY_LIFTED: "Tronçon où le sens unique est levé : les deux sens sont autorisés.",
      ONEWAY_REVERSED: "Tronçon dont le sens est inversé : l'ordre du tracé donne le nouveau sens.",
      EMERGENCY_ONLY: "Axe réservé aux véhicules de secours.",
      HEIGHT_LIMIT: "Portique, arche ou structure : hauteur limite temporaire.",
      COURSE: "L'ordre du tracé donne le sens du parcours (sens des coureurs). Le parcours est fermé au public ; les secours le descendent avec une forte pénalité et ne le remontent qu'en urgence, sur confirmation de l'équipage.",
      CROSSING_POINT: "Carrefour du parcours où les secours peuvent le traverser. Dès qu'un parcours a un point de cisaillement, il ne se traverse plus qu'à ses points actifs ; ailleurs, seulement si la destination est inaccessible et sur confirmation de l'équipage.",
    } as Record<string, string>,
    crossingPointLabel: "Nom du point de cisaillement",
    crossingPointCourse: "Parcours",
    crossingPointNoCourse: "Tracez d'abord un parcours : un point de cisaillement se place sur un parcours.",
    crossingPointPickCourse: "Choisissez le parcours",
    crossingPointSnap: (r: number) => `Le point est recalé sur le carrefour du parcours le plus proche, à moins de ${r} m.`,
    crossingPoints: (n: number) => `${n} point${n > 1 ? "s" : ""} de cisaillement`,
    deleteCourseWithPoints: (n: number) => n > 1 ? `Ses ${n} points de cisaillement seront supprimés avec lui.` : "Son point de cisaillement sera supprimé avec lui.",
    crossingPointsHelp: "Ce parcours ne se traverse qu'à ses points de cisaillement actifs. Supprimer le parcours supprime ses points.",
  },
  poiForm: {
    title: "Point d'intérêt", new: "Nouveau POI", name: "Nom", category: "Catégorie", details: "Détails", validFrom: "Du", validTo: "Au",
    position: "Position", move: "Déplacer", lat: "Latitude", lon: "Longitude", placeAt: "Placer à l'adresse", placeAtHint: "Adresse, rue, commune ou Plus Code",
    deleteConfirm: "Supprimer ce POI ?", saved: "POI enregistré.", deleted: "POI supprimé.",
    noCategory: "Créez d'abord une catégorie.",
  },
  categories: {
    title: "Catégories de POI", new: "Nouvelle catégorie", name: "Nom", color: "Couleur", icon: "Pictogramme", hidden: "Masquée dans les véhicules",
    order: "Ordre", count: (n: number) => `${n} POI`, deleteConfirm: "Supprimer cette catégorie ?", inUse: "Catégorie utilisée par des POI : masquez-la ou déplacez ses POI.",
    icons: { cross: "Croix (PMA, poste de secours)", flag: "Drapeau (PC, point de rendez-vous)", car: "Véhicule", water: "Eau", star: "Étoile", pin: "Repère", tent: "Tente", warning: "Attention" } as Record<string, string>,
  },
  history: {
    title: "Historique", actor: "Auteur", action: "Action", object: "Objet", when: "Date", revision: "Rév.", export: "Exporter en CSV",
    deleteAll: "Effacer l'historique", deleteConfirm: "Effacer tout l'historique de cet événement ? Cette action est elle-même journalisée.",
    empty: "Aucune entrée.", more: "Entrées précédentes", filterType: "Type d'objet", filterAction: "Action", filterActor: "Auteur",
    from: "Du", to: "Au", details: "Détails", before: "Avant", after: "Après", event: "Événement", showOnMap: "Montrer sur la carte",
    gone: "Objet supprimé depuis : rien à montrer.",
    actions: {
      EVENT_CREATED: "Événement créé", EVENT_UPDATED: "Événement modifié", EVENT_ZONE_UPDATED: "Zone modifiée", EVENT_SETTINGS_UPDATED: "Réglages modifiés",
      EVENT_PUBLISHED: "Publié", EVENT_ARCHIVED: "Archivé", EVENT_UNARCHIVED: "Rouvert", EVENT_DELETED: "Événement supprimé", HISTORY_DELETED: "Historique effacé",
      RESTRICTION_CREATED: "Restriction créée", RESTRICTION_UPDATED: "Restriction modifiée", RESTRICTION_ENABLED: "Restriction activée",
      RESTRICTION_DISABLED: "Restriction désactivée", RESTRICTION_DELETED: "Restriction supprimée",
      CATEGORY_CREATED: "Catégorie créée", CATEGORY_UPDATED: "Catégorie modifiée", CATEGORY_DELETED: "Catégorie supprimée",
      POI_CREATED: "POI créé", POI_UPDATED: "POI modifié", POI_DELETED: "POI supprimé", IMPORT_COMMITTED: "Import validé",
      HEIGHT_CORRECTION_CREATED: "Correction de hauteur créée", HEIGHT_CORRECTION_UPDATED: "Correction de hauteur modifiée", HEIGHT_CORRECTION_DELETED: "Correction de hauteur supprimée",
      USER_CREATED: "Compte créé", USER_UPDATED: "Compte modifié", USER_DISABLED: "Compte désactivé", USER_ENABLED: "Compte réactivé", USER_DELETED: "Compte supprimé",
      DEFAULT_CATEGORY_CREATED: "Catégorie par défaut créée", DEFAULT_CATEGORY_UPDATED: "Catégorie par défaut modifiée", DEFAULT_CATEGORY_DELETED: "Catégorie par défaut supprimée",
      USER_PASSWORD_RESET: "Mot de passe réinitialisé", PASSWORD_CHANGED: "Mot de passe changé", SETTINGS_UPDATED: "Paramètres globaux modifiés", SYMBOLOGY_UPDATED: "Symbologie modifiée",
    } as Record<string, string>,
    entity: { EVENT: "Événement", RESTRICTION: "Restriction", POI: "POI", POI_CATEGORY: "Catégorie", FIELD_REPORT: "Signalement" } as Record<string, string>,
  },
  eventSettings: {
    title: "Réglages de l'événement", identity: "Identité", period: "Période", zone: "Zone", zoneHelp: "Zone de l'événement, dessinée sur la carte. Les appareils reçoivent la zone ajustée : enveloppe de la zone et des objets avec une marge de 500 m.",
    drawZone: "Dessiner la zone", redrawZone: "Redessiner la zone", clearZone: "Effacer la zone", noZone: "Aucune zone : la publication est bloquée.",
    options: "Options d'urgence", optionsHelp: "Par défaut, la valeur globale fixée par l'administrateur s'applique. Forcez une option pour cet événement seulement si nécessaire.",
    inherit: "Valeur globale", inheritValue: (v: string) => `Valeur globale (${v})`, on: "Activée", off: "Désactivée",
    tolerance: "Tolérance pour éviter un obstacle souple", toleranceAbs: "Écart absolu (min)", toleranceRel: "Écart relatif (%)",
    toleranceHelp: "Un itinéraire sans obstacle souple est préféré tant qu'il ne rallonge pas le trajet de plus que la tolérance : la plus grande des deux valeurs est retenue.",
    toleranceExample: (minutes: number, d: string) => `Pour un trajet de ${minutes} min, un détour de ${d} au plus sera préféré.`,
    globalValue: (v: string) => `valeur globale : ${v}`,
    saved: "Réglages enregistrés.", overridden: "Réglages propres à l'événement",
    courses: "Parcours",
    coursesHelp: "Un parcours (course, défilé) est fermé au public. Les secours le descendent dans le sens des coureurs avec une forte pénalité et ne le remontent qu'en urgence, sur confirmation de l'équipage.",
    courseTolerance: "Tolérance parcours", courseToleranceAbs: "Écart absolu (min)", courseToleranceRel: "Écart relatif (%)",
    courseToleranceHelp: "Un itinéraire qui évite le parcours est préféré tant qu'il ne rallonge pas le trajet de plus que cette tolérance, plus large que celle des obstacles souples : la plus grande des deux valeurs est retenue. Sans effet en mode urgence quand l'option « Parcours (descente) » est active.",
    courseToleranceExample: (minutes: number, d: string) => `Pour un trajet de ${minutes} min en descendant le parcours, un détour de ${d} au plus sera préféré.`,
    courseCrossing: "Pénalité de traversée (s)",
    courseCrossingHelp: "Temps ajouté à chaque traversée du parcours par un carrefour, dans les deux modes. Entrer sur le parcours ou le quitter n'est pas une traversée.",
    confirmations: "Confirmations de l'équipage",
    confirmationsHelp: "Un passage soumis à confirmation n'est jamais emprunté d'office : l'itinéraire l'évite et, quand le passage fait gagner du temps (en mode normal au-delà de sa tolérance, en mode urgence dès le gain minimal) ou qu'il est le seul accès à la destination, l'équipage se voit proposer l'itinéraire qui l'emprunte. L'accord vaut pour le trajet, par type de passage (par parcours pour une remontée).",
    wrongWayHelp: "Le contresens comprend l'ancien sens d'une rue « double sens pour les secours », y compris en mode normal.",
    passage: "Passage", modeNormal: "Mode normal", modeEmergency: "Mode urgence", notApplicable: "sans objet",
    confirmationMinGain: "Gain minimal (urgence, min)",
    confirmationMinGainHelp: "En mode urgence, un passage soumis à confirmation est proposé dès que l'itinéraire qui l'emprunte fait gagner au moins ce temps.",
    reofferAbs: "Nouvelle proposition : gain supplémentaire (absolu, min)",
    reofferRel: "Nouvelle proposition : gain supplémentaire (relatif, %)",
    reofferHelp: "Pendant un trajet, un type de passage déjà proposé n'est proposé de nouveau que si le gain dépasse le précédent à la fois de l'écart absolu et de l'écart relatif. Règle appliquée par l'application.",
  },
  checklist: {
    title: "Vérifications avant publication", ok: "Prêt à publier", blocked: "Publication bloquée", blockers: "Points bloquants",
    restrictions: "Restrictions par type", pois: "POI", categories: "Catégories", withoutEnd: "Restrictions sans fin propre", outside: "Objets hors zone",
    overridden: "Réglages forcés", packs: "Packs régionaux", noPack: "Aucun pack ne couvre la zone : les appareils n'auront pas de carte.",
  },
  share: {
    title: "Partage avec les équipages", intro: "Les équipages rejoignent l'événement en scannant ce QR code ou en ouvrant le lien sur l'appareil du véhicule.",
    code: "Identifiant", key: "Clé d'accès",
    manual: "Sans QR code, saisissez l'identifiant (8 lettres) et la clé (8 chiffres) dans l'application. Majuscules, minuscules et espaces sont indifférents.", link: "Lien", copyLink: "Copier le lien", print: "Imprimer la fiche", notPublished: "L'événement n'est pas publié : les appareils ne pourront pas le charger avant la publication.",
    packs: "Packs à installer", appLink: "Téléchargement de l'application", sheetTitle: "Secours Nav : rejoindre l'événement", sheetSteps: [
      "Installer Secours Nav sur l'appareil du véhicule (lien ci-dessous).",
      "Scanner le QR code ou ouvrir le lien : l'événement s'ajoute à l'application. Sans QR code, saisir l'identifiant et la clé.",
      "Vérifier que le pack régional est téléchargé avant le départ.",
    ],
  },
  imports: {
    title: "Importer des objets", file: "Fichier", formats: "GeoJSON, KML, KMZ, GPX ou CSV (10 Mo au maximum)", upload: "Analyser", analysing: "Analyse…",
    csvColumns: "Colonnes du CSV", lat: "Latitude", lon: "Longitude", nameCol: "Nom", commentCol: "Commentaire", reanalyse: "Réanalyser avec ces colonnes",
    preview: (n: number) => `${n} objet${n > 1 ? "s" : ""} lus`, target: "Cible", skip: "Ignorer", restriction: "Restriction", poi: "POI",
    mapping: "Affectation par attribut", mappingHelp: "Affectez une cible à chaque valeur d'un attribut, puis ajustez ligne par ligne.", attribute: "Attribut", value: "Valeur",
    commit: (n: number) => `Importer ${n} objet${n > 1 ? "s" : ""}`, committing: "Import…", done: (r: number, p: number) => `${r} restriction${r > 1 ? "s" : ""} et ${p} POI importés.`,
    expires: "Aperçu conservé 24 h.", errors: "Erreurs", snap: "Accrochage", noGeometry: "Sans géométrie : ignoré",
    crossableDefault: "Blocages importés franchissables par les secours", crossableRequired: "Indiquez si les blocages importés sont franchissables.",
    errored: (n: number) => `${n} objet${n > 1 ? "s" : ""} en erreur`, ignoreErrored: (n: number) => `Ignorer les ${n} objet${n > 1 ? "s" : ""} en erreur`,
    erroredHelp: "Sans géométrie exploitable, ou restriction sans voie carrossable à portée d'accrochage : à ignorer ou à affecter en POI.",
    farFromRoad: "Aucune voie carrossable à portée : impossible en restriction", previewOnMap: "Les objets lus sont dessinés en orange sur la carte.",
    invalidRows: (n: number) => `${n} ligne${n > 1 ? "s" : ""} invalide${n > 1 ? "s" : ""} (ignorées)`,
  },
  admin: {
    title: "Administration",
    tabs: { users: "Comptes", categories: "Catégories par défaut", settings: "Paramètres", symbology: "Symbologie", heights: "Corrections de hauteur", packs: "Packs", audit: "Journal" },
    categories: {
      title: "Catégories de POI par défaut",
      help: "Catégories proposées à la création d'un événement. L'opérateur coche celles qu'il veut, elles sont alors copiées dans l'événement. Modifier ou supprimer une catégorie ici ne change pas les événements existants.",
      none: "Aucune catégorie par défaut.", saved: "Catégorie enregistrée.", deleted: "Catégorie supprimée.",
      deleteConfirm: (name: string) => `Supprimer la catégorie par défaut « ${name} » ? Les événements qui l'utilisent la conservent.`,
      CATEGORY_NAME_TAKEN: "Une catégorie par défaut porte déjà ce nom.",
    },
    users: {
      title: "Comptes", new: "Nouveau compte", login: "Identifiant", displayName: "Nom affiché", role: "Rôle", roles: { OPERATOR: "Opérateur", ADMIN: "Administrateur" } as Record<string, string>,
      status: "État", active: "Actif", disabled: "Désactivé", lastLogin: "Dernière connexion", never: "jamais", mustChange: "mot de passe provisoire",
      disable: "Désactiver", enable: "Réactiver", resetPassword: "Réinitialiser le mot de passe", resetConfirm: "Réinitialiser le mot de passe de ce compte ? L'ancien ne fonctionnera plus.",
      provisional: "Mot de passe provisoire", provisionalHelp: "Communiquez-le à la personne par un canal sûr. Il ne sera plus affiché et devra être changé à la première connexion.",
      LOGIN_TAKEN: "Cet identifiant existe déjà.", LAST_ADMIN: "Il doit rester au moins un administrateur actif.", SELF_DEMOTION: "Impossible de désactiver ou de rétrograder son propre compte.",
      SELF_DELETION: "Impossible de supprimer son propre compte.", deleteTitle: "Supprimer le compte",
      deleteConfirm: (name: string, login: string) => `Supprimer le compte « ${name} » (${login}) ? Ses sessions sont fermées et il ne pourra plus se connecter. L'historique conserve ses actions sous « Compte supprimé (${name}) ».`,
      loginHelp: "3 à 40 caractères : lettres, chiffres, point, tiret ou souligné.",
    },
    settings: {
      title: "Paramètres globaux", help: "Valeurs par défaut appliquées à tous les événements qui ne les forcent pas. Elles s'appliquent dès l'enregistrement aux tests d'itinéraire et aux appareils à leur prochaine synchronisation.",
      saved: "Paramètres enregistrés.", updated: "Dernière modification",
    },
    symbology: {
      title: "Symbologie de la carte", help: "Styles des couches des cartes (console et appareils). Les valeurs par défaut sont celles du modèle commun ; un style modifié est signalé.",
      key: "Couche", color: "Couleur", width: "Épaisseur", dash: "Pointillés (ex. 2 1)", opacity: "Opacité", outline: "Contour", overridden: "modifié", resetKey: "Valeur par défaut", saved: "Symbologie enregistrée.",
    },
    heights: {
      title: "Corrections de hauteur globales", help: "Hauteurs limites mesurées sur le terrain, appliquées à tous les événements actuels et futurs dont la zone ajustée les contient. Une hauteur saisie par un opérateur dans un événement prime. Le commentaire est obligatoire.",
      new: "Nouvelle correction", maxHeight: "Hauteur limite (m)", comment: "Commentaire (source, date de mesure)", place: "Placer sur la carte", saved: "Correction enregistrée.", deleted: "Correction supprimée.",
      deleteConfirm: "Supprimer cette correction ? Les événements concernés reviennent à la hauteur de la carte.", revision: "Révision des corrections", none: "Aucune correction.",
    },
    packs: {
      title: "Packs régionaux", help: "Un pack par département : graphe routier, fond de carte et adresses, partagés par la console et les appareils. Mise à jour mensuelle planifiée sur le serveur.",
      id: "Pack", name: "Département", version: "Version", files: "Fichiers", size: "Taille", loaded: "Graphes chargés", none: "Aucun manifeste de packs : graphe unique de développement.",
      update: "Dernière mise à jour planifiée", updateNever: "Aucune mise à jour planifiée n'a encore été exécutée sur ce serveur.",
      states: { RUNNING: "En cours", OK: "Réussie", FAILED: "En échec" } as Record<string, string>, started: "Début", finished: "Fin", producedVersion: "Version produite", showLog: "Journal de la mise à jour",
    },
    audit: { title: "Journal global", help: "Toutes les actions : comptes, paramètres, événements." },
  },
  route: {
    event: "Événement",
    streets: (n: number) => (n === 1 ? "1 voie" : `${n} voies`),
    chooseEvent: "Choisir un événement",
    standardMode: "Navigation standard · aucun événement",
    standardTitle: "Navigation standard",
    standardHelp: "Sans événement, l'itinéraire est celui de l'application sans événement : réseau public, règles habituelles et corrections de hauteur globales, sans mode urgence.",
    setOrigin: "Définir comme départ",
    setDestination: "Définir comme destination",
    newRevision: (r: number) => `Nouvelle révision ${r} de l'événement : recalculez l'itinéraire pour en tenir compte.`,
    recompute: "Recalculer avec la nouvelle révision",
    profile: "Profil de véhicule",
    profileNone: "Saisie libre",
    profileSave: "Enregistrer ce profil",
    profileName: "Nom du profil (ex. VPSP 1)",
    profileDelete: "Supprimer le profil",
    profilesHelp: "Profils conservés dans ce navigateur.",
    time: "Heure",
    now: "Maintenant",
    simulated: "Heure simulée",
    simulatedBanner: (t: string) => `Heure simulée : ${t}. Les statuts, la carte et l'itinéraire correspondent à cette heure.`,
    transitions: "Transitions",
    vehicleHeight: "Hauteur du véhicule (m)",
    mode: "Mode",
    normal: "Normal",
    emergency: "Urgence",
    origin: "Départ",
    destination: "Destination",
    clickHint: "Cliquez sur la carte pour placer le départ puis la destination. Les repères se déplacent à la souris.",
    swap: "Inverser",
    clear: "Effacer",
    compare: "Comparer sans restrictions",
    share: "Copier le lien de ce test",
    copied: "Lien copié",
    computing: "Calcul…",
    result: "Résultat",
    duration: "Durée",
    length: "Distance",
    computeMs: (ms: number) => `calculé en ${ms} ms`,
    maneuvers: "Manœuvres (textes de l'équipage)",
    annotations: "Points d'attention",
    noAnnotation: "Aucun point d'attention.",
    settings: "Réglages appliqués",
    settingsHint: "Ces options s'appliquent en mode urgence. Source : valeur de l'événement, sinon valeur globale.",
    global: "global",
    eventValue: "événement",
    tolerance: "Tolérance",
    without: "Sans restrictions",
    withoutDelta: (d: string) => `Effet du dispositif : ${d}`,
    unreachable: "Destination inaccessible",
    unreachableText: "Itinéraire partiel jusqu'au point le plus proche atteint. Blocages en cause :",
    ignore: "Ignorer ce blocage",
    ignored: "Blocages ignorés pour ce test",
    restore: "Rétablir",
    snapped: (street: string, d: string) => `accroché sur ${street || "une voie sans nom"}, à ${d}`,
    proposalGain: (gain: string, kinds: string) => `Passage proposé : −${gain} (${kinds})`,
    proposalUnreachable: (kinds: string) => `Destination accessible seulement par : ${kinds}`,
    proposalCourses: (kind: string, courses: string) => `${kind} (parcours ${courses})`,
    proposalPoints: (kind: string, points: string) => `${kind} (${points})`,
    authorizedPoints: "Points de cisaillement acceptés pour ce test",
    proposalHelp: "Le moteur n'emprunte jamais d'office un passage soumis à confirmation : il a calculé l'itinéraire qui l'emprunte. Dans le véhicule, l'équipage doit l'accepter ; l'accord vaut pour le trajet.",
    authorizeKind: (kind: string) => `${kind} : autorisé`,
    authorized: "Parcours remontés pour ce test",
    authorizedKinds: "Passages autorisés pour ce test",
    courseTolerance: "Tolérance parcours",
    courseCrossing: "Pénalité de traversée",
    confirmations: (mode: string) => `Confirmations (${mode})`,
    confirmationMinGain: "Gain minimal (urgence)",
    reofferGain: "Nouvelle proposition (gain supplémentaire)",
    error: { SNAP_FAILED: "Point trop loin du réseau routier.", NO_ROUTE: "Aucun itinéraire trouvé." } as Record<string, string>,
    poiShortcut: "Aller à un POI",
    pack: (name: string, id: string, version: string) => `pack ${name} (${id}) du ${version.split("-").reverse().join("/")}`,
    restrictionsTitle: "Restrictions",
    layers: "Couches",
  },
  poi: {
    title: "POI les plus proches du départ",
    category: "Catégorie",
    allCategories: "Toutes catégories",
    needOrigin: "Placez un point de départ pour classer les POI par temps de trajet.",
    none: "Aucun POI actif à cette heure.",
    pick: "Choisir comme destination",
    viaSoft: "par un obstacle souple",
    unreachable: (d: string) => `Non joignable en moins de 30 min (${d} à vol d'oiseau)`,
    computed: (ms: number) => `Classement par temps de trajet, restrictions en vigueur, calculé en ${ms} ms.`,
  },
  decision: {
    NO_SOFT_OBSTACLE: "L'itinéraire le plus rapide ne traverse aucun obstacle souple : il est retenu directement.",
    NO_ROUTE_WITHOUT_SOFT: "La destination n'est accessible qu'en traversant un obstacle souple : la passe A est retenue.",
    UNREACHABLE: "Destination inaccessible, même en traversant les obstacles souples.",
    withinTolerance: (b: string, a: string, d: string, s: string) =>
      `Passe B retenue (sans obstacle souple) : ${b}, contre ${a} pour la passe A. Écart de ${d}, inférieur ou égal à la tolérance de ${s}.`,
    tooSlow: (b: string, a: string, d: string, s: string) =>
      `Passe A retenue (obstacle souple traversé) : ${a}, contre ${b} pour la passe B. Écart de ${d}, supérieur à la tolérance de ${s}.`,
    NO_ROUTE_WITHOUT_COURSE: "La destination n'est accessible qu'en descendant un parcours : la passe C est retenue.",
    COURSE_AVOIDED: "L'itinéraire le plus rapide descend un parcours, mais celui qui l'évite est dans la tolérance parcours : il est retenu.",
    COURSE_DETOUR_TOO_SLOW: "Éviter le parcours coûte plus que la tolérance parcours : la passe C (parcours descendu) est retenue.",
    courseAvoided: (x: string, xT: string, c: string, d: string, s: string) =>
      `${x} retenue (parcours évité) : ${xT}, contre ${c} pour la passe C qui descend le parcours. Écart de ${d}, inférieur ou égal à la tolérance parcours de ${s}.`,
    courseDetourTooSlow: (c: string, xT: string, d: string, s: string) =>
      `Passe C retenue (parcours descendu) : ${c}, contre ${xT} en évitant le parcours. Écart de ${d}, supérieur à la tolérance parcours de ${s}.`,
    passA: "Passe A (obstacles souples autorisés)",
    passB: "Passe B (sans obstacle souple)",
    passC: "Passe C (parcours descendu)",
    passShort: { A_WITH_SOFT: "Passe A", B_WITHOUT_SOFT: "Passe B", C_WITH_COURSE: "Passe C" } as Record<string, string>,
    rule: "Règle",
    courseRule: "Règle parcours",
    softHint: "Obstacle souple : axe rouge quand l'option correspondante n'est pas active, ou signalement non confirmé.",
    courseHint: "Parcours descendu : obstacle souple de second rang, avec la tolérance parcours. Sa remontée n'est jamais choisie par le moteur.",
  },
  annotation: {
    EMERGENCY_CROSSING: "Franchissement secours",
    EMERGENCY_LANE: "Axe rouge",
    UNCONFIRMED_REPORT: "Signalement non confirmé",
    IGNORED_BARRIER: "Blocage ignoré",
    UNKNOWN_HEIGHT: "Hauteur inconnue",
    WRONG_WAY: "Contresens",
    BUS_LANE: "Voie de bus",
    PEDESTRIAN_ZONE: "Zone piétonne",
    EMERGENCY_ONLY_ROAD: "Accès réservé aux secours",
    COURSE: "Parcours",
    COURSE_AGAINST_FLOW: "Remontée du parcours",
    COURSE_CROSSING: "Traversée du parcours",
    CROSSING_POINT: "Point de cisaillement",
    OFF_POINT_CROSSING: "Traversée hors point de cisaillement",
  } as Record<string, string>,
  settingsNames: {
    busLanes: "Voies de bus",
    pedestrianZones: "Zones piétonnes",
    emergencyAccess: "Axes rouges",
    wrongWay: "Contresens",
    crossableBarriers: "Franchissement des blocages franchissables",
    toleranceAbsoluteS: "Tolérance (absolue)",
    toleranceRelative: "Tolérance (relative)",
    courses: "Parcours (descente)",
    courseToleranceAbsoluteS: "Tolérance parcours (absolue)",
    courseToleranceRelative: "Tolérance parcours (relative)",
    courseCrossingPenaltyS: "Pénalité de traversée",
    confirmations: "Confirmations de l'équipage",
    "confirmations.normal": "Confirmations de l'équipage (mode normal)",
    "confirmations.emergency": "Confirmations de l'équipage (mode urgence)",
    confirmationMinGainS: "Gain minimal (urgence)",
    reofferGainAbsoluteS: "Nouvelle proposition : gain supplémentaire (absolu)",
    reofferGainRelative: "Nouvelle proposition : gain supplémentaire (relatif)",
  } as Record<string, string>,
  passageKind: {
    BARRIER_CROSSING: "Franchissement d'un blocage",
    EMERGENCY_LANE: "Axe rouge (voie réservée aux secours)",
    COURSE_WITH_FLOW: "Descente d'un parcours",
    COURSE_CROSSING: "Traversée d'un parcours",
    COURSE_AGAINST_FLOW: "Remontée d'un parcours",
    WRONG_WAY: "Contresens",
    BUS_LANE: "Voie de bus",
    PEDESTRIAN_ZONE: "Zone piétonne",
    CROSSING_POINT: "Passage par un point de cisaillement",
    OFF_POINT_CROSSING: "Traversée d'un parcours hors point de cisaillement",
  } as Record<string, string>,
  proposalReason: {
    GAIN: "Gain de temps",
    UNREACHABLE: "Seul accès à la destination",
  } as Record<string, string>,
  kind: {
    BARRIER: "Blocage",
    ONEWAY_LIFTED: "Sens unique levé",
    ONEWAY_REVERSED: "Sens inversé",
    EMERGENCY_ONLY: "Axe rouge",
    HEIGHT_LIMIT: "Hauteur limite",
    COURSE: "Parcours",
    CROSSING_POINT: "Point de cisaillement",
  } as Record<string, string>,
  crossable: { true: "franchissable par les secours", false: "non franchissable" },
  oldDirection: {
    STRICT: "ancien sens interdit à tous",
    EMERGENCY_WRONG_WAY: "contresens possible en urgence",
    BOTH_WAYS: "double sens pour les secours (dérogation)",
  } as Record<string, string>,
  // EX-UR-07: no wrong way where the public can come the other way
  oldDirectionHint: {
    EMERGENCY_WRONG_WAY: "Le contresens n'est proposé en urgence que si la rue est fermée au public par ailleurs (barrières, axe rouge, rue enclavée). Sinon, l'ancien sens est interdit à tous.",
    BOTH_WAYS: "Dérogation à la règle du contresens : les secours pourront rouler face à la circulation du public, dans les deux modes. À réserver à une voie neutralisée.",
  } as Record<string, string>,
  status: { SCHEDULED: "Programmée", ACTIVE: "Active", ENDED: "Terminée", DISABLED: "Désactivée" } as Record<string, string>,
  warning: {
    FAR_FROM_ROAD: "Point loin de toute route : rien n'est fermé",
    JUNCTION_CENTER: "Point au centre d'un carrefour : branche fermée à vérifier",
    LEVEL_AMBIGUOUS: "Deux niveaux possibles (pont) : niveau à vérifier",
    DIRECTION_ALREADY_FORBIDDEN: "Sens bloqué déjà interdit par le sens unique",
    LINE_NO_CROSSING: "Le trait ne croise aucune route : rien n'est fermé",
    RING_CROSSING_IGNORED: "Le trait croise l'anneau d'un giratoire : l'anneau reste ouvert",
    WAYPOINT_FAR_FROM_ROAD: "Point de passage loin de toute route : ignoré",
    PATH_NOT_FOUND: "Points de passage non reliés par le réseau",
    NOT_A_ONEWAY: "La portion n'est pas en sens unique",
    WRONG_WAY_ON_PUBLIC_ROAD: "Rue ouverte au public : le contresens d'urgence ne sera jamais proposé, l'ancien sens est interdit à tous",
    BOTH_WAYS_ON_PUBLIC_ROAD: "Dérogation : contresens des secours sur une rue ouverte au public",
    PATH_DETOUR: "Tracé beaucoup plus long que la ligne droite : à vérifier",
    OVERLAPS_COURSE: "Recouvre un parcours : le sens du parcours l'emporte",
    NOT_ON_COURSE: "Aucun carrefour du parcours à moins de 30 m : point non appliqué, le parcours ne se traverse plus ici",
  } as Record<string, string>,
  legend: {
    CLOSED_ALL: "Route fermée à tous",
    PUBLIC_CLOSED: "Fermée au public, accès secours",
    ENCLOSED: "Rue enclavée (accès par un dispositif)",
    INACCESSIBLE: "Rue inaccessible",
    EMERGENCY_ACCESS: "Axe rouge (réservé aux secours)",
    ONEWAY_LIFTED: "Sens unique levé",
    ONEWAY_REVERSED: "Sens inversé (flèches : nouveau sens)",
    COURSE: "Parcours (flèches : sens des coureurs)",
    COURSE_UPCOMING: "Parcours à venir (pas encore en vigueur)",
    CROSSING_POINT: "Point de cisaillement (vide : fermé)",
    HEIGHT_OVERRIDE: "Hauteur limite temporaire",
    SOFT_OBSTACLE: "Signalement non confirmé",
    BARRIER_CUT: "Point de blocage",
    barrierNonCrossable: "Blocage non franchissable",
    barrierCrossable: "Blocage franchissable par les secours",
    route: "Itinéraire retenu",
    alternative: "Autre passe",
    without: "Sans restrictions",
    pois: "Points d'intérêt",
    drawn: "Objets saisis par l'opérateur",
    effects: "Effets sur le réseau",
    operator: "Saisie de l'opérateur",
    title: "Légende",
    showSymbols: "Afficher les symboles",
    hideSymbols: "Masquer les symboles",
    hiddenInVehicles: "masquée dans les véhicules",
    hc: "Correction de hauteur globale",
    zone: "Zone de l'événement",
    zoneAdjusted: "Zone ajustée",
    heightLimit: "Hauteur limite",
    emergencyOnly: "Réservé aux secours",
    course: "Parcours (flèches : sens des coureurs)",
    searchResult: "Résultat de recherche",
  } as Record<string, string>,
  join: {
    title: "Rejoindre un événement",
    text: "Pour rejoindre cet événement, installez Secours Nav sur l'appareil du véhicule.",
    download: "Télécharger l'application",
    open: "Ouvrir dans Secours Nav",
    desktop: "Sur un ordinateur, ouvrez ce lien sur l'appareil du véhicule.",
    scan: "Ou scannez ce QR code avec l'appareil du véhicule.",
    manual: "Saisie manuelle dans l'application",
    code: "Identifiant", key: "Clé d'accès",
  },
  appPage: {
    title: "Application Secours Nav",
    version: "Version",
    date: "Date",
    size: "Taille",
    sha: "Empreinte SHA-256",
    download: "Télécharger l'APK",
    none: "Aucune version disponible pour le moment.",
    privacy: "Politique de confidentialité",
    install: [
      "Android 10 ou plus récent.",
      "Autoriser l'installation d'applications inconnues pour le navigateur utilisé.",
      "Une version compilée depuis Android Studio porte une autre signature : la désinstaller avant d'installer cette version.",
    ],
  },
  // with the attribution of the style source (© OpenStreetMap, Protomaps): BAN addresses (Licence Ouverte 2.0) and notices
  attribution: 'Adresses <a href="https://adresse.data.gouv.fr" target="_blank" rel="noopener">BAN</a> (Licence Ouverte 2.0) · <a href="/licences.txt" target="_blank">Licences</a>',
  errors: {
    network: "Serveur injoignable.",
    VALIDATION_FAILED: "Saisie invalide",
    EVENT_NOT_FOUND: "Événement inconnu",
    OUTSIDE_PACKS: "Hors de la zone couverte par les packs régionaux (Hauts-de-France)",
    UNAUTHENTICATED: "Connexion requise.",
    FORBIDDEN: "Vous n'avez pas le droit d'effectuer cette action.",
    CSRF_REJECTED: "Requête refusée (origine).",
    VERSION_CONFLICT: "Modifié entre-temps par un autre opérateur",
    EVENT_ARCHIVED: "Événement archivé : lecture seule.",
    ALREADY_PUBLISHED: "L'événement est déjà publié.",
    PUBLISH_BLOCKED: "Publication bloquée",
    CONFIRMATION_MISMATCH: "Le nom saisi ne correspond pas.",
    CATEGORY_IN_USE: "Catégorie utilisée par des POI.",
    RESTRICTION_NOT_FOUND: "Restriction inconnue (supprimée entre-temps ?).",
    POI_NOT_FOUND: "POI inconnu (supprimé entre-temps ?).",
    CATEGORY_NOT_FOUND: "Catégorie inconnue.",
    HEIGHT_CORRECTION_NOT_FOUND: "Correction inconnue.",
    IMPORT_INVALID: "Fichier d'import invalide",
    IMPORT_TOO_LARGE: "Fichier de plus de 10 Mo.",
    IMPORT_EXPIRED: "L'aperçu a expiré : renvoyez le fichier.",
    IMPORT_DONE: "Cet import a déjà été validé.",
    SNAPSHOT_REQUIRED: "Resynchronisation complète nécessaire.",
    LOGIN_TAKEN: "Cet identifiant existe déjà.",
    LAST_ADMIN: "Il doit rester au moins un administrateur actif.",
    SELF_DEMOTION: "Impossible de modifier ainsi son propre compte.",
    INVALID_KEY: "Clé d'accès invalide.",
    SNAP_FAILED: "Aucune voie carrossable à portée d'accrochage",
    RATE_LIMITED: "Trop de requêtes : réessayez dans une minute.",
    INTERNAL_ERROR: "Erreur du serveur.",
  } as Record<string, string>,
};
